import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SHOP } from "../data/content";
import SectionHeading from "../components/SectionHeading";
import useReveal from "../hooks/useReveal";

/**
 * ShopPage — /shop
 * Editorial storefront for creative & therapeutic products.
 * No checkout yet — products gather early interest before printing.
 * Structure scales: add categories/products in SHOP (data/content.js).
 */
export default function ShopPage() {
    useReveal();
    return (
        <div className="pt-24" data-testid="shop-page">
            {/* HEADER */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                <div className="ssh-reveal max-w-3xl">
                    <SectionHeading
                        eyebrow="The Shop"
                        title="Objects for"
                        titleItalic="the inner world."
                        sub="Thoughtfully made companions for your healing practice — journals, and soon, creative kits and art-based tools. The shop opens gently: first with early-interest lists, before any printing begins."
                    />
                </div>
            </section>

            {/* CATEGORIES */}
            {SHOP.categories.map((cat) => {
                const products = SHOP.products.filter(
                    (p) => p.category === cat.slug,
                );
                return (
                    <section
                        key={cat.slug}
                        className="bg-[#FAF8F5] border-t border-[#EFEBE4]"
                        data-testid={`shop-category-${cat.slug}`}
                    >
                        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 ssh-reveal">
                                <div>
                                    <p className="ssh-eyebrow">
                                        Category 01
                                    </p>
                                    <h2 className="mt-4 font-serif font-light tracking-tight leading-[1.05] text-4xl lg:text-5xl text-[#2C2B29]">
                                        {cat.name}
                                    </h2>
                                </div>
                                <p className="max-w-md text-base leading-relaxed text-[#4A4947]">
                                    {cat.tagline}
                                </p>
                            </div>

                            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {products.map((p, i) => (
                                    <Link
                                        key={p.slug}
                                        to={`/shop/${p.slug}`}
                                        className="group ssh-card overflow-hidden flex flex-col ssh-reveal"
                                        style={{
                                            transitionDelay: `${0.06 * i}s`,
                                        }}
                                        data-testid={`shop-product-${p.slug}`}
                                    >
                                        <div className="bg-[#F4F0FA] p-8 lg:p-10">
                                            <img
                                                src={p.coverImage}
                                                alt={`${p.name} cover artwork`}
                                                className="w-full h-auto object-contain shadow-[0_18px_50px_-20px_rgba(44,43,41,0.35)] group-hover:-translate-y-1 transition-transform duration-500"
                                                loading="lazy"
                                            />
                                        </div>
                                        <div className="p-8 flex flex-col flex-1">
                                            <div className="flex items-start justify-between gap-4">
                                                <p className="ssh-eyebrow">
                                                    {cat.name}
                                                </p>
                                                <span className="text-[10px] uppercase tracking-[0.18em] text-[#7C6AAE] border border-[#D9CFEC] bg-[#F4F0FA] px-2 py-1 whitespace-nowrap">
                                                    {p.status}
                                                </span>
                                            </div>
                                            <h3 className="mt-4 font-serif text-2xl lg:text-3xl text-[#2C2B29] leading-tight group-hover:text-[#A68A6B] transition-colors">
                                                {p.name}
                                            </h3>
                                            <p className="mt-3 text-sm text-[#4A4947] leading-relaxed">
                                                {p.tagline}
                                            </p>
                                            <p className="mt-4 text-xs uppercase tracking-[0.18em] text-[#75736F]">
                                                Expected from{" "}
                                                {p.options[0].priceLabel}
                                            </p>
                                            <div className="mt-auto pt-6">
                                                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A68A6B] group-hover:gap-3 transition-all">
                                                    Discover the journal
                                                    <ArrowRight
                                                        className="h-3.5 w-3.5"
                                                        strokeWidth={1.5}
                                                    />
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>

                            <p className="mt-10 text-sm italic text-[#75736F] ssh-reveal">
                                More journals, creative kits, and art-based
                                wellbeing pieces are quietly in the making.
                            </p>
                        </div>
                    </section>
                );
            })}
        </div>
    );
}
